<template>
  <div ref="rootRef" class="searchable-select" :class="{ 'searchable-select--open': open }">
    <button
      type="button"
      class="searchable-select__trigger"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :aria-label="ariaLabel || placeholder"
      :id="triggerId"
      @click="toggle"
      @keydown.down.prevent="openAndFocusSearch"
      @keydown.enter.prevent="toggle"
    >
      <span
        v-if="selectedOption?.color"
        class="searchable-select__swatch"
        :style="{ background: selectedOption.color }"
        aria-hidden="true"
      ></span>
      <span class="searchable-select__label" :class="{ 'searchable-select__label--placeholder': !selectedOption }">
        {{ selectedOption?.label ?? placeholder }}
      </span>
      <svg class="searchable-select__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div
      v-if="open"
      class="searchable-select__panel"
      role="listbox"
      :aria-labelledby="triggerId"
    >
      <div v-if="searchable" class="searchable-select__search">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          ref="searchRef"
          v-model="query"
          type="text"
          :placeholder="searchPlaceholder"
          autocomplete="off"
          @keydown.escape="close"
          @keydown.down.prevent="moveActive(1)"
          @keydown.up.prevent="moveActive(-1)"
          @keydown.enter.prevent="selectActive"
        />
      </div>

      <ul ref="listRef" class="searchable-select__list">
        <li v-if="filteredOptions.length === 0" class="searchable-select__empty">
          {{ emptyText }}
        </li>
        <li
          v-for="(opt, index) in filteredOptions"
          :key="opt.value"
          role="option"
          :aria-selected="opt.value === modelValue"
          class="searchable-select__option"
          :class="{
            'searchable-select__option--active': index === activeIndex,
            'searchable-select__option--selected': opt.value === modelValue,
          }"
          @mouseenter="activeIndex = index"
          @click="choose(opt)"
        >
          <span v-if="opt.color" class="searchable-select__swatch" :style="{ background: opt.color }" aria-hidden="true"></span>
          <span class="searchable-select__option-label">{{ opt.label }}</span>
          <svg
            v-if="opt.value === modelValue"
            class="searchable-select__check"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
          </svg>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"

export interface SelectOption {
  value: string
  label: string
  color?: string
}

interface Props {
  modelValue: string
  options: SelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  ariaLabel?: string
  searchable?: boolean
  id?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: "Select…",
  searchPlaceholder: "Search…",
  emptyText: "No matches",
  searchable: true,
  ariaLabel: "",
  id: "",
})

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void
}>()

const open = ref(false)
const query = ref("")
const activeIndex = ref(0)
const rootRef = ref<HTMLDivElement | null>(null)
const searchRef = ref<HTMLInputElement | null>(null)
const listRef = ref<HTMLUListElement | null>(null)

const triggerId = computed(() => props.id || `searchable-select-${Math.random().toString(36).slice(2, 8)}`)

const selectedOption = computed(() => props.options.find((o) => o.value === props.modelValue) || null)

const filteredOptions = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return props.options
  return props.options.filter((o) => o.label.toLowerCase().includes(q))
})

watch(filteredOptions, () => {
  activeIndex.value = 0
})

function toggle() {
  if (open.value) close()
  else openAndFocusSearch()
}

function openAndFocusSearch() {
  open.value = true
  query.value = ""
  activeIndex.value = Math.max(
    0,
    filteredOptions.value.findIndex((o) => o.value === props.modelValue),
  )
  nextTick(() => {
    if (props.searchable) searchRef.value?.focus()
  })
}

function close() {
  open.value = false
}

function choose(opt: SelectOption) {
  emit("update:modelValue", opt.value)
  close()
}

function moveActive(delta: number) {
  if (filteredOptions.value.length === 0) return
  const len = filteredOptions.value.length
  activeIndex.value = (activeIndex.value + delta + len) % len
  scrollActiveIntoView()
}

function selectActive() {
  const opt = filteredOptions.value[activeIndex.value]
  if (opt) choose(opt)
}

function scrollActiveIntoView() {
  nextTick(() => {
    const list = listRef.value
    if (!list) return
    const el = list.children[activeIndex.value] as HTMLElement | undefined
    el?.scrollIntoView({ block: "nearest" })
  })
}

function onDocumentClick(e: MouseEvent) {
  if (!rootRef.value) return
  if (!rootRef.value.contains(e.target as Node)) close()
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape" && open.value) close()
}

onMounted(() => {
  document.addEventListener("mousedown", onDocumentClick)
  document.addEventListener("keydown", onKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onDocumentClick)
  document.removeEventListener("keydown", onKeydown)
})
</script>

<style scoped>
.searchable-select {
  position: relative;
  width: 100%;
}

.searchable-select__trigger {
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: var(--surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: var(--text-base);
  text-align: left;
  cursor: pointer;
  transition: border-color var(--transition-fast);
}

.searchable-select__trigger:focus-visible {
  outline: none;
  border-color: var(--accent);
}

.searchable-select--open .searchable-select__trigger {
  border-color: var(--accent);
}

.searchable-select__swatch {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.searchable-select__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.searchable-select__label--placeholder {
  color: var(--text-tertiary);
}

.searchable-select__chevron {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: var(--text-tertiary);
  transition: transform var(--transition-fast);
}

.searchable-select--open .searchable-select__chevron {
  transform: rotate(180deg);
}

.searchable-select__panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: var(--z-dropdown, 1000);
  background: var(--surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  max-height: min(320px, 60vh);
  overflow: hidden;
}

.searchable-select__search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--border-subtle);
  color: var(--text-tertiary);
}

.searchable-select__search svg {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
}

.searchable-select__search input {
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  color: var(--text-primary);
  font-family: var(--font-body);
  /* 16px stops iOS Safari from zooming the viewport on focus */
  font-size: 16px;
  min-height: 32px;
}

.searchable-select__list {
  list-style: none;
  margin: 0;
  padding: 4px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.searchable-select__option {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  padding: 8px 10px;
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-size: var(--text-sm);
  cursor: pointer;
}

.searchable-select__option--active {
  background: var(--bg-secondary);
}

.searchable-select__option--selected {
  color: var(--accent);
  font-weight: 550;
}

.searchable-select__option-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.searchable-select__check {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: var(--accent);
}

.searchable-select__empty {
  padding: 16px 12px;
  text-align: center;
  color: var(--text-tertiary);
  font-size: var(--text-sm);
}
</style>
